import axios from 'axios';
import useAuthStore from '../store/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard-stats'),
  getAttacks: () => api.get('/attacks'),
};

// Assets API
export const assetsAPI = {
  getAll: (params) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
};

// Alerts API
export const alertsAPI = {
  getAll: (params) => api.get('/alerts', { params }),
  updateStatus: (id, status) => api.patch(`/alerts/${id}`, { status }),
};

// Security Score API
export const securityScoreAPI = {
  get: () => api.get('/security-score'),
  getHistory: (days) => api.get('/security-score/history', { params: { days } }),
};

// Vulnerabilities API
export const vulnerabilitiesAPI = {
  getAll: (params) => api.get('/vulnerabilities', { params }),
};

// Compliance API
export const complianceAPI = {
  getAll: () => api.get('/compliance'),
};

// NAC API
export const nacAPI = {
  getDevices: () => api.get('/nac/devices'),
  deviceAction: (deviceId, action) => api.post(`/nac/devices/${deviceId}/action`, null, { params: { action } }),
};

// Threats/IDS API
export const threatsAPI = {
  getEvents: (params) => api.get('/threats', { params }),
};

// Automation API
export const automationAPI = {
  getPlaybooks: () => api.get('/automation/playbooks'),
  executePlaybook: (id) => api.post(`/automation/playbooks/${id}/execute`),
};

// Compliance Controls API
export const complianceControlsAPI = {
  getControls: (framework) => api.get(`/compliance/${framework}/controls`),
};

// Settings API
export const settingsAPI = {
  updateProfile: (data) => api.put('/settings/profile', data),
};

// SOC Alert Operations
export const socAPI = {
  assignAlert: (id) => api.post(`/alerts/${id}/assign`),
  investigateAlert: (id) => api.post(`/alerts/${id}/investigate`),
  addNote: (id, note) => api.post(`/alerts/${id}/notes`, { note }),
  updateAlert: (id, data) => api.patch(`/alerts/${id}`, data),
  getWarRoom: () => api.get('/alerts/war-room'),
  getMyQueue: () => api.get('/alerts/my-queue'),
};

// Reports API
export const reportsAPI = {
  getCompliancePDF: (framework) => api.get('/reports/compliance/pdf', { params: framework ? { framework } : {}, responseType: 'blob' }),
  getVulnPDF: (severity) => api.get('/reports/vulnerabilities/pdf', { params: severity ? { severity } : {}, responseType: 'blob' }),
  getAlertsPDF: (status) => api.get('/reports/alerts/pdf', { params: status ? { status } : {}, responseType: 'blob' }),
};

// NVD Sync
export const nvdAPI = {
  sync: () => api.post('/vulnerabilities/sync-nvd'),
};

export default api;
